import React, { useState, useEffect } from 'react';
import Project from '../../components/Project';
import SkeletonLoader from '../../components/SkeletonLoader';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchProjects, fetchProjectSkills } from '../../api/projectApi';
import MetaTagsUpdater from '../../components/MetaTagsUpdater';
import NoContentMessage from '../../components/NoContentMessage'; // Import NoContentMessage

function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const { addToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const projectsPerPage = 12;

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const fetchProjectsAndSkills = async () => {
      try {
        setLoading(true);

        const projectsResponse = await fetchProjects({
          paginate: true,
          per_page: projectsPerPage,
          page: currentPage
        });

        if (!projectsResponse.success) {
          addToast('error', projectsResponse.message || 'Failed to fetch projects.');
          setLoading(false);
          setProjects([]);
          setTotalPages(1);
          return;
        }

        const fetchedProjectsData = projectsResponse.data;
        const paginationMeta = projectsResponse.pagination;

        setTotalPages(paginationMeta.last_page || 1);

        if (fetchedProjectsData.length === 0) {
            setProjects([]);
            setLoading(false);
            return;
        }

        const projectsWithSkillsPromises = fetchedProjectsData.map(async (project) => {
          if (!project.slug) {
            console.warn(`Project with ID ${project.id} is missing a slug. Skipping skills fetch.`);
            return { ...project, technologies: [] };
          }
          const skillsResponse = await fetchProjectSkills(project.slug);
          if (skillsResponse.success) {
            const technologies = Array.isArray(skillsResponse.data) ? skillsResponse.data.map(skill => skill.name) : [];
            return {
              ...project,
              technologies: technologies,
            };
          } else {
            console.warn(`Failed to fetch skills for project ${project.slug}: ${skillsResponse.message}`);
            return { ...project, technologies: [] };
          }
        });

        const projectsWithSkills = await Promise.all(projectsWithSkillsPromises);
        setProjects(projectsWithSkills);

      } catch (e) {
        console.error("Error fetching projects or skills:", e);
        addToast('error', 'Failed to load projects. Please try again later.');
        setProjects([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectsAndSkills();
  }, [addToast, location.search, currentPage]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      const newQueryParams = new URLSearchParams(location.search);
      newQueryParams.set('page', pageNumber);
      navigate(`?${newQueryParams.toString()}`);
    }
  };

  return (
    <div className="pt-20 bg-bg-dark min-h-screen text-text-gray">
      <MetaTagsUpdater pageKey="Projects" />
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-center text-text-white mb-16 relative after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-20 after:h-1 after:bg-accent-red after:rounded-sm after:bottom-[-15px]">
            My Projects
          </h1>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            <SkeletonLoader type="project-card" count={projectsPerPage} />
          </div>
        ) : projects.length > 0 ? (
          <>
            <Project projects={projects} />
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-2 mt-12">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded-md bg-gray-700 text-text-white hover:bg-accent-red disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => handlePageChange(i + 1)}
                    className={`px-4 py-2 rounded-md ${
                      currentPage === i + 1 ? 'bg-accent-red text-white' : 'bg-gray-700 text-text-white hover:bg-gray-600'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded-md bg-gray-700 text-text-white hover:bg-accent-red disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <NoContentMessage message="No projects found." />
        )}
      </div>
    </div>
  );
}

export default Projects;